import { useCallback, useEffect, useMemo } from 'react';

import { usePersistedSetting } from '@@common';

const PINNED_FLOW_INSTANCES_STORAGE_KEY = 'flow-instances-panel.pinnedInstanceIdsByDefinition';
const PINNED_FLOW_INSTANCES_CHANGED_EVENT = 'flow-instances-panel.pinnedInstanceIdsChanged';

type PinnedFlowInstancesByDefinition = Record<string, string[]>;

function readPinnedFlowInstances() {
	try {
		const stored = localStorage.getItem(PINNED_FLOW_INSTANCES_STORAGE_KEY);
		return stored ? (JSON.parse(stored) as PinnedFlowInstancesByDefinition) : {};
	} catch {
		return {};
	}
}

export default function usePinnedFlowInstances(definitionId?: string | null) {
	const [pinnedByDefinition, setPinnedByDefinition] = usePersistedSetting<PinnedFlowInstancesByDefinition>(PINNED_FLOW_INSTANCES_STORAGE_KEY, {});

	useEffect(() => {
		const handlePinnedInstancesChanged = (event: Event) => {
			const detail = (event as CustomEvent<PinnedFlowInstancesByDefinition>).detail;
			setPinnedByDefinition(detail ?? readPinnedFlowInstances());
		};

		window.addEventListener(PINNED_FLOW_INSTANCES_CHANGED_EVENT, handlePinnedInstancesChanged);
		return () => window.removeEventListener(PINNED_FLOW_INSTANCES_CHANGED_EVENT, handlePinnedInstancesChanged);
	}, [setPinnedByDefinition]);

	const pinnedInstanceIds = useMemo(() => {
		if (!definitionId) {
			return [];
		}

		return pinnedByDefinition[definitionId] ?? [];
	}, [definitionId, pinnedByDefinition]);

	const setPinnedInstanceIds = useCallback(
		(updater: (current: string[]) => string[]) => {
			if (!definitionId) {
				return;
			}

			const current = pinnedByDefinition[definitionId] ?? [];
			const nextIds = Array.from(new Set(updater(current)));
			const nextByDefinition = { ...pinnedByDefinition };

			if (nextIds.length === 0) {
				delete nextByDefinition[definitionId];
			} else {
				nextByDefinition[definitionId] = nextIds;
			}

			setPinnedByDefinition(nextByDefinition);
			window.dispatchEvent(new CustomEvent(PINNED_FLOW_INSTANCES_CHANGED_EVENT, { detail: nextByDefinition }));
		},
		[definitionId, pinnedByDefinition, setPinnedByDefinition],
	);

	const pinInstance = useCallback(
		(instanceId: string) => {
			setPinnedInstanceIds((current) => (current.includes(instanceId) ? current : [instanceId, ...current]));
		},
		[setPinnedInstanceIds],
	);

	const unpinInstance = useCallback(
		(instanceId: string) => {
			setPinnedInstanceIds((current) => current.filter((id) => id !== instanceId));
		},
		[setPinnedInstanceIds],
	);

	const togglePin = useCallback(
		(instanceId: string) => {
			setPinnedInstanceIds((current) => (current.includes(instanceId) ? current.filter((id) => id !== instanceId) : [instanceId, ...current]));
		},
		[setPinnedInstanceIds],
	);

	const clearPinnedInstances = useCallback(() => {
		setPinnedInstanceIds(() => []);
	}, [setPinnedInstanceIds]);

	const isPinned = useCallback((instanceId: string) => pinnedInstanceIds.includes(instanceId), [pinnedInstanceIds]);

	return {
		pinnedInstanceIds,
		pinInstance,
		unpinInstance,
		togglePin,
		clearPinnedInstances,
		isPinned,
	};
}
